import {useEffect, useRef, useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {useFormik} from 'formik'
import {toAbsoluteUrl} from '../../../_metronic/helpers'
import {useTranslation} from 'react-i18next'
import {initialValues} from './_module'
import {useAppDispatch} from '../../../redux/hooks'
import {showErrorMessage, showSuccessMessage} from '_metronic/helpers/swalHelper'
import CustomSelect from 'app/custom/CustomSelect'
import {
  Prefix,
  studyMode,
  studyModeFr,
  StudySponsor,
  StudySponsorFr,
} from '_metronic/helpers/jsonData'
import DatePicker, {DateObject} from 'react-multi-date-picker'
import gregorian from 'react-date-object/calendars/gregorian'
import ImageCropperModal from 'app/custom/ImageCropperModal'
import Loader from 'app/custom/loader/Loader'
import {GetOnlineProps, RelatedIntakes, SubmitKankorApp} from '../../../redux/Slice/generalSlice'
import {encryptId} from '_metronic/helpers/Encrypt'
import ShowSuccessMsgEntrance from './ShowSuccessMsgEntrance'
import SignatureCanvas from 'react-signature-canvas'

export function OnlineKankor() {
  const {i18n} = useTranslation()
  const {t} = useTranslation()
  const [loader, setLoader] = useState(true)
  const [genders, setGenders] = useState([])
  const [countries, setCountries] = useState([])
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState<boolean>(false)
  const [croppedImage, setCroppedImage] = useState<string>('')
  const [currentImg, setCurrentImage] = useState('0')
  const [file, setFile] = useState<any>(null)
  const [programs, setPrograms] = useState<any>([])
  const [academicYears, setAcademicYears] = useState<any>([])
  const [intakes, setIntakes] = useState<any>([])
  const [sections, setSections] = useState<any>([])
  const [loading, setLoading] = useState(false)
  const dispatch = useAppDispatch()
  const [successMsg, setSuccessMsg] = useState(false)
  const sigCanvas = useRef<SignatureCanvas | null>(null)

  const handleCropComplete = (croppedImage: string) => {
    setCroppedImage(croppedImage)

    const imageData = atob(croppedImage.split(',')[1])
    const arrayBuffer = new ArrayBuffer(imageData.length)
    const uintArray = new Uint8Array(arrayBuffer)

    for (let i = 0; i < imageData.length; i++) {
      uintArray[i] = imageData.charCodeAt(i)
    }

    const blob = new Blob([arrayBuffer], {type: 'image/jpeg'})
    const file = new File([blob], 'image.png', {type: 'image/png'})
    setFile(file)
  }

  const validationSchema = Yup.object().shape({
    prefix: Yup.string().required(t('VALIDATION.SELECT_', {name: t('LABEL.PREFIX')})),
    first_name: Yup.string().required(t('VALIDATION.REQUIRED_', {name: t('LABEL.FIRST_NAME')})),
    last_name: Yup.string().required(t('VALIDATION.REQUIRED_', {name: t('LABEL.LAST_NAME')})),
    gender_id: Yup.string().required(t('VALIDATION.SELECT_', {name: t('LABEL.GENDER')})),
    birth_date: Yup.string()
      .trim()
      .required(t('VALIDATION.SELECT_', {name: t('LABEL.BIRTH_DATE')})),
    contact_number: Yup.string()
      .required(t('VALIDATION.REQUIRED_', {name: t('LABEL.CONTACT_NUMBER')}))
      .matches(/^[0-9]+$/, t('VALIDATION.INVALID_CONTACT_NUMBER')),
    email: Yup.string()
      .required(t('VALIDATION.REQUIRED_', {name: t('PROFILE.EMAIL')}))
      .email(t('PROFILE.EMAIL_FORMAT')),
    nationality_id: Yup.string().required(t('VALIDATION.SELECT_', {name: t('LABEL.NATIONALITY')})),
    permanent_country_id: Yup.string().required(
      t('VALIDATION.SELECT_', {name: t('LABEL.COUNTRY')})
    ),
    permanent_city: Yup.string().required(t('VALIDATION.REQUIRED_', {name: t('LABEL.CITY')})),
    program_id: Yup.string().required(t('VALIDATION.SELECT_', {name: t('LABEL.PROGRAM')})),
    academic_year_id: Yup.string().required(
      t('VALIDATION.SELECT_', {name: t('LABEL.ACADEMIC_YEAR')})
    ),
    intake_id: Yup.string().required(t('VALIDATION.SELECT_', {name: t('LABEL.INTAKE')})),
    sponsor_type: Yup.string().required(t('VALIDATION.SELECT_', {name: t('LABEL.STUDY_SPONSOR')})),
    study_mode: Yup.string().required(t('VALIDATION.SELECT_', {name: t('LABEL.STUDY_MODE')})),
    section_id: Yup.string().required(t('VALIDATION.SELECT_', {name: t('LABEL.SECTION')})),
  })

  const formik = useFormik({
    initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values, {setSubmitting, resetForm}) => {
      const dataURL = sigCanvas.current?.toDataURL('image/png')

      if (sigCanvas.current?.isEmpty() || !dataURL) {
        showErrorMessage(t('VALIDATION.REQUIRED_', {name: t('GLOBAL.SIGNATURE')}))
        return
      }

      // Convert Base64 to raw binary
      const base64 = dataURL.split(',')[1]
      const binary = atob(base64)
      const array = new Uint8Array(binary.length)

      for (let i = 0; i < binary.length; i++) {
        array[i] = binary.charCodeAt(i)
      }

      // Create Blob and File
      const blob = new Blob([array], {type: 'image/png'})
      const file = new File([blob], 'signature.png', {type: 'image/png'})

      setLoading(true)
      const formData = new FormData()
      Object.entries(values).forEach(([key, val]) => {
        formData.append(key, val as string)
      })
      formData.append('signature', file)
      formData.append('campus_id', '17')
      formData.append('lang', i18n.language)
      formData.append('photo', file)

      dispatch(SubmitKankorApp(formData))
        .unwrap()
        .then(() => {
          setSuccessMsg(true)
          resetForm()
          setFile(null)
          setCurrentImage('0')
          setCroppedImage('')
          showSuccessMessage(t('MESSAGES.SUBMITTED'))
        })
        .catch((error: any) => {
          console.log('Submission error:', error)
          const duplicateMessage = error?.errors?.email?.[0] || error?.email?.[0] || error?.message
          if (duplicateMessage) showErrorMessage(t('VALIDATION.DUPLICATE_RECORD'))
          else showErrorMessage(t('MESSAGES.SERVER_ERROR'))
        })
        .finally(() => {
          setLoading(false)
          setSubmitting(false)
        })
    },
  })

  const handleChangeLanguage = (lang) => {
    i18n.changeLanguage(lang)
    window.location.reload()
  }

  const languages = [
    {
      lang: 'fr',
      name: 'Français',
      flag: toAbsoluteUrl('/media/flags/france.svg'),
    },
    {
      lang: 'en',
      name: 'English',
      flag: toAbsoluteUrl('/media/flags/united-states.svg'),
    },
  ]

  const currentLanguage = languages.find((x) => x.lang === i18n.language)

  const sm = i18n.language === 'fr' ? studyModeFr : studyMode
  const ss = i18n.language === 'fr' ? StudySponsorFr : StudySponsor

  useEffect(() => {
    dispatch(GetOnlineProps(currentLanguage?.lang || 'fr')).then((res: any) => {
      if (res.meta.requestStatus === 'fulfilled') {
        setGenders(res.payload.genders)
        setCountries(res.payload.countries)
        setAcademicYears(res.payload.academic_years)
        setIntakes(res.payload.intakes)
        setSections(res.payload.sections)
        setPrograms(res.payload.programs)
        setLoader(false)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleProChange = (programId: any) => {
    formik.setFieldValue('program_id', programId)
    if (programId) {
      dispatch(RelatedIntakes(encryptId(programId))).then((res: any) => {
        if (res.meta.requestStatus === 'fulfilled') setIntakes(res.payload)
      })
    } else setIntakes([])
  }

  const clearSignature = () => {
    sigCanvas.current?.clear()
  }

  if (successMsg) return <ShowSuccessMsgEntrance />

  return (
    <>
      <div className='card pt-5 container mb-5'>
        <div className='card-header bg-white border-bottom d-flex align-items-center justify-content-between gap-3 px-3 px-md-5 py-3'>
          <img src={toAbsoluteUrl('/media/logo/dakar-logo.png')} style={{maxWidth: '130px'}} />

          <div
            className='btn-group rounded-pill overflow-hidden'
            role='group'
            aria-label='Language selector'
          >
            {languages.map((language) => (
              <button
                key={language.lang}
                type='button'
                onClick={() => handleChangeLanguage(language.lang)}
                className={clsx(
                  'btn btn-sm px-2 py-1 d-flex align-items-center gap-1 fw-bold rounded-pill',
                  {
                    'btn-primary': i18n.language === language.lang,
                    'btn-outline-secondary': i18n.language !== language.lang,
                  }
                )}
              >
                <img
                  src={language.flag}
                  alt=''
                  width='22'
                  height='15'
                  className='rounded-pill object-fit-cover'
                />

                <span className='d-none d-sm-inline'>{language.name}</span>
              </button>
            ))}
          </div>
        </div>
        <div className='card-body p-5 p-md-9'>
          <h3 className='text-center mb-4' style={{color: '#8c1d1d'}}>
            {currentLanguage?.lang === 'fr'
              ? 'Étudiez à Dakar au Sénégal'
              : 'Study in Dakar, Senegal'}
          </h3>

          {currentLanguage?.lang === 'en' && (
            <p className='mb-5'>
              <span className='fw-bold text-primary'>
                Welcome to the official SWISS UMEF University - Campus Dakar online application
                platform.
              </span>
              <br />
              Please complete this form carefully using your official information, exactly as it
              appears on your passport.
              <br />
              Ensure that all data entered is accurate and complete. Once the form has been
              submitted, our Admissions Office or one of our authorised representatives will review
              your application.
              <br />
              You will then be contacted for document verification and the next steps in the
              admission process. Only complete applications will be considered.
              <br />
              <span className='fw-bold'>Important:</span> Submitting this application does not
              create any financial obligation.
              <br />
              Admission fees will only be requested after official confirmation of your acceptance.
            </p>
          )}

          {currentLanguage?.lang === 'fr' && (
            <p className='mb-5'>
              <span className='fw-bold text-primary'>
                Bienvenue sur la plateforme officielle de candidature de SWISS UMEF University -
                Campus Dakar.
              </span>
              <br />
              Merci de remplir soigneusement ce formulaire en ligne avec vos informations
              officielles, exactement telles qu’elles figurent sur votre passeport.
              <br />
              Veuillez vous assurer que toutes les données saisies sont exactes et complètes. Une
              fois le formulaire validé, notre service des admissions ou l’un de nos représentants
              officiels examinera votre dossier.
              <br />
              Vous serez ensuite contacté(e) pour la vérification de vos documents et la suite du
              processus d’admission. Seules les candidatures complètes seront prises en compte.
              <br />
              <span className='fw-bold'>Important :</span> Le dépôt de cette candidature n’implique
              aucun engagement financier.
              <br />
              Les frais d’admission ne seront demandés qu’après confirmation officielle de votre
              acceptation.
            </p>
          )}

          {/* Fees section */}
          <div className='border rounded p-3 bg-light'>
            <div className='row g-3'>
              {/* Administrative fees */}
              <div className='col-12 col-lg-4'>
                <div className='h-100 bg-white border rounded p-3'>
                  <h5
                    className='fw-bold text-center border-bottom pb-2 mb-3'
                    style={{color: '#8c1d1d'}}
                  >
                    {currentLanguage?.lang === 'fr'
                      ? 'Frais administratifs'
                      : 'Administrative Fees'}
                  </h5>

                  <div className='d-flex justify-content-between gap-2 mb-2'>
                    <span>
                      {currentLanguage?.lang === 'fr'
                        ? 'Concours d’entrée :'
                        : 'Entrance examination:'}
                    </span>
                    <strong className='text-nowrap'>
                      {currentLanguage?.lang === 'fr' ? 'gratuit' : 'Free'}
                    </strong>
                  </div>

                  <div className='d-flex justify-content-between gap-2 mb-2'>
                    <span>
                      {currentLanguage?.lang === 'fr' ? 'Inscription :' : 'Registration fee:'}
                    </span>
                    <strong className='text-nowrap'>220 000 FCFA/an</strong>
                  </div>

                  <div className='d-flex justify-content-between gap-2'>
                    <span>
                      {currentLanguage?.lang === 'fr'
                        ? 'Centre d’entrepreneuriat :'
                        : 'Entrepreneurship Centre fee:'}
                    </span>
                    <strong className='text-nowrap'>60 000 FCFA</strong>
                  </div>
                </div>
              </div>

              {/* Licence */}
              <div className='col-12 col-lg-4'>
                <div className='h-100 bg-white border rounded p-3'>
                  <h5
                    className='fw-bold text-center border-bottom pb-2 mb-3'
                    style={{color: '#8c1d1d'}}
                  >
                    {currentLanguage?.lang === 'fr'
                      ? 'Licence - 3 ans'
                      : 'Bachelor’s Degree - 3 Years'}
                  </h5>

                  <div className='text-center mb-3'>
                    <div className='fs-4 fw-bold text-primary'>2 500 000 FCFA/an</div>
                  </div>

                  <div className='small mb-2'>
                    <strong>{currentLanguage?.lang === 'fr' ? 'Paiement :' : 'Payment:'}</strong>{' '}
                    {currentLanguage?.lang === 'fr'
                      ? 'jusqu’à 10 mensualités'
                      : 'up to 10 monthly instalments'}
                  </div>

                  <div className='small'>
                    <strong>{currentLanguage?.lang === 'fr' ? 'Admission :' : 'Admission:'}</strong>{' '}
                    {currentLanguage?.lang === 'fr'
                      ? 'sur dossier et examen d’entrée - minimum 50/100'
                      : 'based on application review and entrance examination - minimum 50/100'}
                  </div>
                </div>
              </div>

              {/* Master */}
              <div className='col-12 col-lg-4'>
                <div className='h-100 bg-white border rounded p-3'>
                  <h5
                    className='fw-bold text-center border-bottom pb-2 mb-3'
                    style={{color: '#8c1d1d'}}
                  >
                    {currentLanguage?.lang === 'fr'
                      ? 'Master - 2 ans'
                      : 'Master’s Degree - 2 Years'}
                  </h5>

                  <div className='d-flex justify-content-between gap-2 mb-2'>
                    <span>
                      {currentLanguage?.lang === 'fr' ? 'Tarif standard' : 'Standard tuition'}
                    </span>
                    <strong className='text-nowrap text-decoration-line-through text-danger'>
                      7 000 000 FCFA
                    </strong>
                  </div>

                  <div
                    className='fw-bold text-center py-1 px-2 rounded mb-2'
                    style={{
                      color: '#8c1d1d',
                      backgroundColor: '#f8eaea',
                    }}
                  >
                    {currentLanguage?.lang === 'fr'
                      ? 'Offre spéciale pour la rentrée 2026'
                      : 'Special offer for the 2026 intake'}
                  </div>

                  <div className='d-flex justify-content-between gap-2 small mb-1'>
                    <span>
                      {currentLanguage?.lang === 'fr' ? 'Gestion et Droit' : 'Management and Law'}
                    </span>
                    <strong className='text-nowrap'>4 000 000 FCFA</strong>
                  </div>

                  <div className='d-flex justify-content-between gap-2 small mb-1'>
                    <span>
                      {currentLanguage?.lang === 'fr' ? 'Sciences politiques' : 'Political Science'}
                    </span>
                    <strong className='text-nowrap'>4 400 000 FCFA</strong>
                  </div>

                  <div className='d-flex justify-content-between gap-2 small mb-2'>
                    <span>
                      {currentLanguage?.lang === 'fr'
                        ? 'Sciences et technologies'
                        : 'Science and Technology'}
                    </span>
                    <strong className='text-nowrap'>5 000 000 FCFA</strong>
                  </div>

                  <div className='small border-top pt-2'>
                    <strong>{currentLanguage?.lang === 'fr' ? 'Paiement :' : 'Payment:'}</strong>{' '}
                    {currentLanguage?.lang === 'fr'
                      ? 'jusqu’à 20 mensualités'
                      : 'up to 20 monthly instalments'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='card mb-5 mt-0 container'>
        <div className='card-body p-5'>
          {!loader && (
            <form
              className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
              noValidate
              autoComplete='off'
              onSubmit={formik.handleSubmit}
            >
              {/* First row personal information */}
              <div className='row col-12'>
                <h3>{t('LABEL.PERSONAL_INFORMATION')}</h3>
                <hr />
                <div className='row'>
                  <div className='col-md-10'>
                    <div className='row'>
                      <div className='col-sm-6'>
                        <label className='col-form-label fw-bolder required'>
                          {t('LABEL.PREFIX')}
                        </label>
                        <CustomSelect
                          name='prefix'
                          className='input'
                          onChange={(prefix: any) => {
                            formik.setFieldValue('prefix', prefix.id)
                            if (prefix.id === 'Mr.') formik.setFieldValue('gender_id', 1)
                            else if (prefix.id === 'Mrs.' || prefix.id === 'Ms.')
                              formik.setFieldValue('gender_id', 2)
                            else formik.setFieldValue('gender_id', '')
                          }}
                          value={formik.values.prefix}
                          options={Prefix}
                          placeholderText={t('LABEL.PREFIX')}
                        />
                        {formik.touched.prefix && formik.errors.prefix && (
                          <span className='text-danger'>{formik.errors.prefix}</span>
                        )}
                      </div>
                      <div className='col-sm-6'>
                        <label className='col-form-label fw-bolder required'>
                          {t('LABEL.GENDER')}
                        </label>
                        <CustomSelect
                          name='gender_id'
                          className='input'
                          onChange={(gender_id: any) =>
                            formik.setFieldValue('gender_id', gender_id.id)
                          }
                          value={formik.values.gender_id}
                          options={genders}
                          placeholderText={t('LABEL.GENDER')}
                        />
                        {formik.touched.gender_id && formik.errors.gender_id && (
                          <span className='text-danger'>{formik.errors.gender_id}</span>
                        )}
                      </div>
                      <div className='col-md-4 col-sm-6'>
                        <label className='col-form-label fw-bolder required'>
                          {t('LABEL.FIRST_NAME')}
                        </label>
                        <input
                          type='text'
                          placeholder={t('LABEL.FIRST_NAME')}
                          {...formik.getFieldProps('first_name')}
                          className={clsx(
                            'form-control form-control-sm text-capitalize',
                            {
                              'is-invalid': formik.touched.first_name && formik.errors.first_name,
                            },
                            {
                              'is-valid': formik.touched.first_name && !formik.errors.first_name,
                            }
                          )}
                        />
                        {formik.touched.first_name && formik.errors.first_name && (
                          <span className='text-danger'>{formik.errors.first_name}</span>
                        )}
                      </div>
                      <div className='col-md-4 col-sm-6'>
                        <label className='col-form-label fw-bolder required'>
                          {t('LABEL.LAST_NAME')}
                        </label>
                        <input
                          type='text'
                          placeholder={t('LABEL.LAST_NAME')}
                          {...formik.getFieldProps('last_name')}
                          className={clsx(
                            'form-control form-control-sm text-uppercase',
                            {
                              'is-invalid': formik.touched.last_name && formik.errors.last_name,
                            },
                            {
                              'is-valid': formik.touched.last_name && !formik.errors.last_name,
                            }
                          )}
                        />
                        {formik.touched.last_name && formik.errors.last_name && (
                          <span className='text-danger'>{formik.errors.last_name}</span>
                        )}
                      </div>
                      <div className='col-md-4 col-sm-6'>
                        <label className='col-form-label fw-bolder required'>
                          {t('LABEL.NATIONALITY')}
                        </label>
                        <CustomSelect
                          name='nationality_id'
                          className='input'
                          onChange={(nationality: any) =>
                            formik.setFieldValue('nationality_id', nationality.id)
                          }
                          value={formik.values.nationality_id}
                          options={countries}
                          placeholderText={t('LABEL.NATIONALITY')}
                        />
                        {formik.touched.nationality_id && formik.errors.nationality_id && (
                          <span className='text-danger'>{formik.errors.nationality_id}</span>
                        )}
                      </div>

                      <div className='col-md-4 col-sm-6'>
                        <label className='col-form-label fw-bolder required'>
                          {t('LABEL.BIRTH_DATE')}
                        </label>
                        <DatePicker
                          calendar={gregorian}
                          containerStyle={{width: '100%'}}
                          value={formik.values.birth_date}
                          placeholder={t('LABEL.BIRTH_DATE')}
                          style={{
                            width: '100%',
                            height: '40px',
                            boxSizing: 'border-box',
                          }}
                          onChange={(e: any) => {
                            if (e) {
                              formik.setFieldValue(
                                'birth_date',
                                `${e.day}-${e.month.number}-${e.year}`
                              )
                            } else formik.setFieldValue('birth_date', '')
                          }}
                          format='DD-MM-YYYY'
                          onOpenPickNewDate={false}
                          maxDate={new DateObject()}
                        />

                        {formik.touched.birth_date && formik.errors.birth_date && (
                          <span className='text-danger'>{formik.errors.birth_date}</span>
                        )}
                      </div>

                      <div className='col-md-4 col-sm-6'>
                        <label className='col-form-label fw-bolder required'>
                          {t('LABEL.CONTACT_NUMBER')}
                        </label>
                        <input
                          type='text'
                          placeholder={t('LABEL.CONTACT_NUMBER')}
                          {...formik.getFieldProps('contact_number')}
                          className={clsx(
                            'form-control form-control-sm',
                            {
                              'is-invalid':
                                formik.touched.contact_number && formik.errors.contact_number,
                            },
                            {
                              'is-valid':
                                formik.touched.contact_number && !formik.errors.contact_number,
                            }
                          )}
                        />
                        {formik.touched.contact_number && formik.errors.contact_number && (
                          <span className='text-danger'>{formik.errors.contact_number}</span>
                        )}
                      </div>
                      <div className='col-md-4 col-sm-6'>
                        <label className='col-form-label fw-bolder required'>
                          {t('PROFILE.EMAIL')}
                        </label>
                        <input
                          type='email'
                          placeholder={t('PROFILE.EMAIL')}
                          {...formik.getFieldProps('email')}
                          className={clsx(
                            'form-control form-control-sm',
                            {
                              'is-invalid': formik.touched.email && formik.errors.email,
                            },
                            {
                              'is-valid': formik.touched.email && !formik.errors.email,
                            }
                          )}
                        />
                        {formik.touched.email && formik.errors.email && (
                          <span className='text-danger'>{formik.errors.email}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className='col-md-2'>
                    <div className='col-12 mt-5 text-center'>
                      <label className='mx-5 mt-2 fw-bolder w-100'>{t('LABEL.PHOTO')}</label>
                      <label
                        className='mx-5 mt-2'
                        onClick={() => {
                          setIsAvatarModalOpen(true)
                        }}
                        style={{width: '100%'}}
                      >
                        {!croppedImage ? (
                          <img
                            src={
                              currentImg === '0'
                                ? toAbsoluteUrl('/media/avatars/photo.jpg')
                                : process.env.REACT_APP_API_URL + '/' + currentImg
                            }
                            className='img-fluid img-thumbnail'
                            alt='user-profile'
                            style={{maxWidth: 180}}
                          />
                        ) : (
                          <img
                            src={croppedImage}
                            className='img-fluid img-thumbnail image-view'
                            alt='user-profile'
                            style={{maxWidth: 180}}
                          />
                        )}
                      </label>
                      <ImageCropperModal
                        key={croppedImage}
                        title={t('LABEL.PHOTO')}
                        isOpen={isAvatarModalOpen}
                        onRequestClose={() => setIsAvatarModalOpen(false)}
                        onCropComplete={handleCropComplete}
                        image={croppedImage}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* second row permanent address */}
              <div className='row col-12'>
                <h3 className='mt-5'>{t('LABEL.PERMANENT_ADDRESS')}</h3>
                <hr />
                <div className='row'>
                  <div className='col-md-5 col-sm-6'>
                    <label className='col-form-label fw-bolder'>{t('LABEL.STREET_NAME_NO')}</label>
                    <input
                      type='text'
                      placeholder={t('LABEL.STREET_NAME_NO')}
                      {...formik.getFieldProps('permanent_address')}
                      className='form-control form-control-sm'
                    />
                  </div>
                  <div className='col-md-2 col-sm-6'>
                    <label className='col-form-label fw-bolder'>{t('LABEL.ZIP_CODE')}</label>
                    <input
                      type='text'
                      placeholder={t('LABEL.ZIP_CODE')}
                      {...formik.getFieldProps('permanent_zip_code')}
                      className='form-control form-control-sm'
                    />
                  </div>
                  <div className='col-md-2 col-sm-6'>
                    <label className='col-form-label fw-bolder required'>{t('LABEL.CITY')}</label>
                    <input
                      type='text'
                      placeholder={t('LABEL.CITY')}
                      {...formik.getFieldProps('permanent_city')}
                      className={clsx(
                        'form-control form-control-sm',
                        {
                          'is-invalid':
                            formik.touched.permanent_city && formik.errors.permanent_city,
                        },
                        {
                          'is-valid':
                            formik.touched.permanent_city && !formik.errors.permanent_city,
                        }
                      )}
                    />
                    {formik.touched.permanent_city && formik.errors.permanent_city && (
                      <span className='text-danger'>{formik.errors.permanent_city}</span>
                    )}
                  </div>
                  <div className='col-md-3 col-sm-6'>
                    <label className='col-form-label fw-bolder required'>
                      {t('LABEL.COUNTRY')}
                    </label>
                    <CustomSelect
                      name='permanent_country_id'
                      className='input'
                      onChange={(permanent_country_id: any) =>
                        formik.setFieldValue('permanent_country_id', permanent_country_id.id)
                      }
                      value={formik.values.permanent_country_id}
                      options={countries}
                      placeholderText={t('LABEL.COUNTRY')}
                    />
                    {formik.touched.permanent_country_id && formik.errors.permanent_country_id && (
                      <span className='text-danger'>{formik.errors.permanent_country_id}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* fifth row program*/}
              <div className='row col-12'>
                <h3 className='mt-5'>{t('LABEL.PROGRAM')}</h3>
                <hr />
                <div className='row'>
                  <div className='col-sm-12 col-md-4'>
                    <label className='col-form-label fw-bolder required'>
                      {t('LABEL.ACADEMIC_YEAR')}
                    </label>
                    <CustomSelect
                      name='academic_year_id'
                      className='input'
                      onChange={(academic_year_id: any) =>
                        formik.setFieldValue('academic_year_id', academic_year_id.id)
                      }
                      value={formik.values.academic_year_id}
                      options={academicYears}
                      placeholderText={t('LABEL.ACADEMIC_YEAR')}
                    />
                    {formik.touched.academic_year_id && formik.errors.academic_year_id && (
                      <span className='text-danger'>{formik.errors.academic_year_id}</span>
                    )}
                  </div>
                  <div className='col-sm-12 col-md-4'>
                    <label className='col-form-label fw-bolder required'>
                      {t('LABEL.PROGRAM')}
                    </label>
                    <CustomSelect
                      name='program_id'
                      className='input'
                      onChange={(obj: any) => handleProChange(obj.id)}
                      value={formik.values.program_id}
                      options={programs}
                      placeholderText={t('LABEL.PROGRAM')}
                    />
                    {formik.touched.program_id && formik.errors.program_id && (
                      <span className='text-danger'>{formik.errors.program_id}</span>
                    )}
                  </div>
                  <div className='col-sm-12 col-md-4'>
                    <label className='col-form-label fw-bolder required'>{t('LABEL.INTAKE')}</label>
                    <CustomSelect
                      name='intake_id'
                      className='input'
                      onChange={(intake_id: any) => formik.setFieldValue('intake_id', intake_id.id)}
                      value={formik.values.intake_id}
                      options={intakes}
                      placeholderText={t('LABEL.INTAKE')}
                    />
                    {formik.touched.intake_id && formik.errors.intake_id && (
                      <span className='text-danger'>{formik.errors.intake_id}</span>
                    )}
                  </div>

                  <div className='col-sm-12 col-md-4'>
                    <label className='col-form-label fw-bolder required'>
                      {t('LABEL.SECTION')}
                    </label>
                    <CustomSelect
                      name='section_id'
                      className='input'
                      onChange={(section: any) => formik.setFieldValue('section_id', section.id)}
                      value={formik.values.section_id}
                      options={sections}
                      placeholderText={t('LABEL.SECTION')}
                    />
                    {formik.touched.section_id && formik.errors.section_id && (
                      <span className='text-danger'>{formik.errors.section_id}</span>
                    )}
                  </div>
                  <div className='col-sm-12 col-md-4'>
                    <label className='col-form-label fw-bolder required'>
                      {t('LABEL.STUDY_MODE')}
                    </label>
                    <CustomSelect
                      name='study_mode'
                      className='input'
                      onChange={(mode: any) => formik.setFieldValue('study_mode', mode.id)}
                      value={formik.values.study_mode}
                      options={sm}
                      placeholderText={t('LABEL.STUDY_MODE')}
                    />
                    {formik.touched.study_mode && formik.errors.study_mode && (
                      <span className='text-danger'>{formik.errors.study_mode}</span>
                    )}
                  </div>
                  <div className='col-sm-12 col-md-4'>
                    <label className='col-form-label fw-bolder required'>
                      {t('LABEL.STUDY_SPONSOR_ONLINE')}
                    </label>
                    <CustomSelect
                      name='sponsor_type'
                      className='input'
                      onChange={(sponsor_type: any) =>
                        formik.setFieldValue('sponsor_type', sponsor_type.id)
                      }
                      value={formik.values.sponsor_type}
                      options={ss}
                      placeholderText={t('LABEL.STUDY_SPONSOR_ONLINE')}
                    />
                    {formik.touched.sponsor_type && formik.errors.sponsor_type && (
                      <span className='text-danger'>{formik.errors.sponsor_type}</span>
                    )}
                  </div>
                </div>
                <div className='col-sm-12 col-md-4 mt-3'>
                  <div className='d-flex justify-content-between align-items-center mb-2'>
                    <label className='col-form-label label fw-bolder required mb-0'>
                      {t('GLOBAL.SIGNATURE')}
                    </label>

                    <button
                      type='button'
                      onClick={clearSignature}
                      className='btn btn-sm btn-link text-danger fw-bold text-decoration-none p-0'
                    >
                      <span className='fa fa-times mx-2'></span>
                      <span>{t('LABEL.CLEAR')}</span>
                    </button>
                  </div>

                  <SignatureCanvas
                    ref={sigCanvas}
                    penColor='#406e89'
                    canvasProps={{
                      width: 500,
                      height: 200,
                      className: 'border border-gray-300 rounded w-100',
                    }}
                  />
                </div>
              </div>

              {/* sixth Row */}
              <div className='row col-12 shadow-sm m-1 p-5 mt-5'>
                <p className='fw-bold text-danger'>{t('MESSAGES.CONFIRMATION_MESSAGE')}</p>
                <div className='d-flex'>
                  <button
                    type='submit'
                    className='btn btn-sm btn-light btn-primary fw-bolder'
                    disabled={formik.isSubmitting || loading}
                  >
                    {!loading && (
                      <span className='indicator-label'>
                        <i className='fas fa-save mx-2'></i>
                        {t('BUTTONS.SAVE')}
                      </span>
                    )}
                    {loading && (
                      <span className='indicator-progress' style={{display: 'block'}}>
                        {t('GLOBAL.LOADING')}
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    )}
                  </button>
                  <div className='fs-6 fw-bold text-gray-700'>
                    <div />
                  </div>
                </div>
              </div>
            </form>
          )}
          {loader && <Loader />}
        </div>
      </div>
    </>
  )
}
